import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, FileText, Image, Archive, Trash2, ExternalLink } from 'lucide-react';
import { attachmentsAPI } from '../services/api';
import toast from 'react-hot-toast';

const AttachmentUpload = ({ shipmentId, onUploadSuccess, attachments = [], disabled = false }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return <Image className="h-5 w-5" />;
    if (mimeType === 'application/pdf') return <FileText className="h-5 w-5" />;
    if (mimeType.includes('word') || mimeType.includes('excel') || mimeType.includes('powerpoint')) {
      return <FileText className="h-5 w-5" />;
    }
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <Archive className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = useCallback((files) => {
    const validFiles = Array.from(files).filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length + selectedFiles.length > 5) {
      toast.error('Maximum 5 files allowed.');
      return;
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  }, [selectedFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [handleFileSelect]);

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (!shipmentId) {
      toast.error('Please create the shipment first before uploading attachments');
      return;
    }

    setUploading(true);
    try {
      const result = await attachmentsAPI.upload(shipmentId, selectedFiles);
      toast.success(result.message);
      setSelectedFiles([]);
      if (onUploadSuccess) {
        onUploadSuccess(result.attachments);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (attachment) => {
    if (attachment.download_url) {
      window.open(attachment.download_url, '_blank');
    } else {
      toast.error('Download link not available');
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await attachmentsAPI.delete(attachmentId);
      toast.success('Attachment deleted successfully');
      if (onUploadSuccess) {
        // Refresh attachments list
        const result = await attachmentsAPI.getByShipment(shipmentId);
        onUploadSuccess(result.attachments);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete attachment');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver && !disabled
            ? 'border-blue-500 bg-blue-50'
            : disabled
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
      >
        <Upload className={`h-8 w-8 mx-auto mb-2 ${disabled ? 'text-gray-400' : 'text-gray-400'}`} />
        {disabled ? (
          <div>
            <p className="text-sm text-gray-500 mb-2">
              Create the shipment first to upload attachments
            </p>
            <p className="text-xs text-gray-400">
              You can add attachments after the shipment is created
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop files here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-500">
              Maximum 5 files, 10MB each. Supported: Images, PDFs, Documents, Archives
            </p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => !disabled && handleFileSelect(e.target.files)}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
          disabled={disabled}
        />
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && !disabled && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected Files:</h4>
          {selectedFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex items-center space-x-2">
                {getFileIcon(file.type)}
                <span className="text-sm">{file.name}</span>
                <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
              </div>
              <button
                type="button"
                onClick={() => removeSelectedFile(index)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      )}

      {/* Existing Attachments */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Attachments:</h4>
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                <div className="flex items-center space-x-3">
                  {getFileIcon(attachment.mime_type)}
                  <div>
                    <p className="text-sm font-medium">{attachment.original_filename}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(attachment.file_size)} • Uploaded by {attachment.uploaded_by_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="p-1 text-blue-600 hover:text-blue-800"
                    title="Click to open file"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleDelete(attachment.id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentUpload; 